import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  CurrenciesFilterParams,
  CurrencyType,
  CurrenciesListResponse,
  CreateCurrencyParams,
  UpdateCurrencyParams
} from '@/types/currency'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import {
  fetchCurrencies,
  fetchCurrency,
  updateCurrency,
  deleteCurrency,
  createCurrency,
  bulkDeleteCurrencies,
  sortCurrency
} from '@/lib/api'
import { SortEnum, SortOrderEnum } from '@/enums'

// Single currency state type
type CurrencyStateType = {
  isLoading: boolean
  response: CurrencyType | null
  error: {
    status?: number
    message: string
  } | null
}

// Currencies list state type
type CurrenciesListStateType = {
  isLoading: boolean
  response: CurrenciesListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type CurrenciesStoreState = {
  // Currencies list filters state
  filters: CurrenciesFilterParams
  setFilters: (filters: Partial<CurrenciesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Currencies list state
  currenciesListState: CurrenciesListStateType
  fetchCurrencies: (params?: CurrenciesFilterParams) => Promise<CurrenciesListResponse>
  resetCurrenciesList: () => void

  // Single currency state
  currencyState: CurrencyStateType
  getCurrency: (currencyId: string) => Promise<CurrencyType>
  createCurrency: (params: CreateCurrencyParams) => Promise<CurrencyType>
  updateCurrency: (currencyId: string, params: UpdateCurrencyParams) => Promise<CurrencyType>
  deleteCurrency: (currencyId: string) => Promise<void>
  resetCurrency: () => void

  // Selection operations
  selectedCurrencyIds: string[]
  selectCurrency: (id: string) => void
  deselectCurrency: (id: string) => void
  toggleCurrencySelection: (id: string) => void
  selectAllCurrencies: (currencies: CurrencyType[]) => void
  deselectAllCurrencies: () => void
  isCurrencySelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteCurrencies: (ids: string[]) => Promise<BulkDeleteResponse>

  // Sort operation
  sortCurrency: (currencyId: string, type: SortOrderEnum) => Promise<CurrencyType>
}

const defaultFilters: CurrenciesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'order'
}

const initialCurrencyState: CurrencyStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialCurrenciesListState: CurrenciesListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useCurrenciesStore = create<CurrenciesStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    currencyState: initialCurrencyState,
    currenciesListState: initialCurrenciesListState,
    selectedCurrencyIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof CurrenciesFilterParams]
          } else {
            updatedFilters[key as keyof CurrenciesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: CurrenciesFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value as CurrenciesFilterParams['sortBy']
            break
          case 'active':
            filters.active = value === 'true'
            break
          default:
            filters[key as keyof CurrenciesFilterParams] = value as any
            break
        }
      })

      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else {
            params.set(key, value.toString())
          }
        }
      })

      return params
    },

    // Single currency operations
    getCurrency: async (currencyId: string) => {
      const currentState = get().currencyState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        currencyState: {
          ...initialCurrencyState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchCurrency(currencyId)

        set(() => ({
          currencyState: {
            ...initialCurrencyState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          currencyState: {
            ...initialCurrencyState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createCurrency: async (params: CreateCurrencyParams) => {
      set(() => ({
        currencyState: {
          ...initialCurrencyState,
          isLoading: true
        }
      }))

      try {
        const data = await createCurrency(params)

        set(() => ({
          currencyState: {
            ...initialCurrencyState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          currencyState: {
            ...initialCurrencyState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    updateCurrency: async (currencyId: string, params: UpdateCurrencyParams) => {
      set(() => ({
        currencyState: {
          ...get().currencyState,
          isLoading: true
        }
      }))

      try {
        const data = await updateCurrency(currencyId, params)

        set(() => ({
          currencyState: {
            ...get().currencyState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          currencyState: {
            ...initialCurrencyState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteCurrency: async (currencyId: string) => {
      set(() => ({
        currencyState: {
          ...get().currencyState,
          isLoading: true
        }
      }))

      try {
        await deleteCurrency(currencyId)

        set(() => ({
          currencyState: initialCurrencyState
        }))
      } catch (error: any) {
        set(() => ({
          currencyState: {
            ...initialCurrencyState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetCurrency: () => {
      set(() => ({
        currencyState: initialCurrencyState
      }))
    },

    // Currencies list operations
    fetchCurrencies: async (params?: CurrenciesFilterParams) => {
      const currentState = get().currenciesListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        currenciesListState: {
          ...initialCurrenciesListState,
          isLoading: true
        }
      }))

      try {
        const filtersToUse = params || get().filters
        const data = await fetchCurrencies(filtersToUse)

        set(() => ({
          currenciesListState: {
            ...initialCurrenciesListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          currenciesListState: {
            ...initialCurrenciesListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetCurrenciesList: () => {
      set(() => ({
        currenciesListState: initialCurrenciesListState
      }))
    },

    // Selection operations
    selectCurrency: id => {
      set(state => {
        if (state.selectedCurrencyIds.includes(id)) {
          return state
        }
        return { selectedCurrencyIds: [...state.selectedCurrencyIds, id] }
      })
    },

    deselectCurrency: id => {
      set(state => ({
        selectedCurrencyIds: state.selectedCurrencyIds.filter(currencyId => currencyId !== id)
      }))
    },

    toggleCurrencySelection: id => {
      set(state => {
        if (state.selectedCurrencyIds.includes(id)) {
          return { selectedCurrencyIds: state.selectedCurrencyIds.filter(currencyId => currencyId !== id) }
        }
        return { selectedCurrencyIds: [...state.selectedCurrencyIds, id] }
      })
    },

    selectAllCurrencies: currencies => {
      set(() => ({
        selectedCurrencyIds: currencies.map(currency => currency.id)
      }))
    },

    deselectAllCurrencies: () => {
      set(() => ({ selectedCurrencyIds: [] }))
    },

    isCurrencySelected: id => {
      return get().selectedCurrencyIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteCurrencies: async ids => {
      set(state => ({
        currencyState: {
          ...state.currencyState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteCurrencies({ ids })

        set(state => ({
          currencyState: {
            ...state.currencyState,
            isLoading: false
          },
          selectedCurrencyIds: state.selectedCurrencyIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          currencyState: {
            ...state.currencyState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    },

    sortCurrency: async (currencyId: string, type: SortOrderEnum) => {
      set(state => ({ currencyState: { ...state.currencyState, isLoading: true } }))

      try {
        const data = await sortCurrency(currencyId, type)

        set(state => ({
          currencyState: { ...state.currencyState, isLoading: false, response: data }
        }))

        return data
      } catch (error: any) {
        set(state => ({
          currencyState: {
            ...state.currencyState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default useCurrenciesStore
